// Coloring Creator Page Component
'use client';

import React, { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import TemplateCard from '@/components/coloring/TemplateCard';
import TemplateFilters from '@/components/coloring/TemplateFilters';
import MontessoriToggle from '@/components/montessori/MontessoriToggle';
import { Palette } from 'lucide-react';

interface Template {
  id: string;
  slug: string;
  title: string;
  svgUrl?: string | null;
  pngUrl?: string | null;
  premium: boolean;
  montessoriFocus?: string | null;
  tags: string[];
}

export default function ColoringCreatePage() {
  const { data: session } = useSession();
  const router = useRouter();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [montessoriMode, setMontessoriMode] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  const isSubscriber = session?.user?.isSubscriber || false;

  useEffect(() => {
    fetchTemplates();
  }, [selectedAge, selectedTheme]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedAge) params.set('age', selectedAge);
      if (selectedTheme) params.set('theme', selectedTheme);

      const response = await fetch(`/api/coloring/templates?${params}`);
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMontessoriToggle = (enabled: boolean) => {
    if (!isSubscriber) {
      setShowUpsell(true);
      return;
    }
    setMontessoriMode(enabled);
  };

  const handleTemplateSelect = (template: Template) => {
    const params = new URLSearchParams();
    params.set('template', template.slug);
    if (selectedAge) params.set('age', selectedAge);
    if (montessoriMode) params.set('montessori', 'true');
    if (template.montessoriFocus) params.set('focus', template.montessoriFocus);

    router.push(`/coloring/canvas?${params}`);
  };

  const handlePremiumClick = () => {
    setShowUpsell(true);
  };

  const filteredTemplates = templates.filter(t => {
    if (selectedTheme) {
      return t.tags.some(tag => tag.toLowerCase().includes(selectedTheme));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" style={{ marginBottom: '1.5rem' }}>
          <ol style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
            <li>
              <a href="/" style={{ color: '#7C3AED', textDecoration: 'none', fontWeight: 600 }}>
                Home
              </a>
            </li>
            <li aria-hidden="true">▸</li>
            <li>
              <a href="/coloring" style={{ color: '#7C3AED', textDecoration: 'none', fontWeight: 600 }}>
                Coloring
              </a>
            </li>
            <li aria-hidden="true">▸</li>
            <li aria-current="page" style={{ fontWeight: 600, color: '#444' }}>
              Create
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-4 flex items-center justify-center gap-3">
            <Palette size={48} className="text-green-600" />
            <h1 className="text-4xl font-bold text-gray-800 md:text-5xl">
              Kids' Interactive Coloring Pages
            </h1>
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#666', marginBottom: '1rem' }}>
            Montessori & Age-Friendly Templates
          </h2>
          <p className="text-lg text-gray-600">
            Choose a template and start your creative journey
          </p>
          
          {/* Credit Cost Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1), rgba(56, 249, 215, 0.1))',
            borderRadius: '50px',
            border: '2px solid rgba(67, 233, 123, 0.3)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#065f46',
          }}>
            ✨ 1 credit per coloring book
          </div>
        </div>

        {/* Montessori Toggle */}
        <div className="mx-auto mb-8 max-w-2xl">
          <MontessoriToggle
            enabled={montessoriMode}
            isSubscriber={isSubscriber}
            onChange={handleMontessoriToggle}
            onUpsell={() => setShowUpsell(true)}
          />
        </div>

        {/* Filters */}
        <TemplateFilters
          selectedAge={selectedAge}
          selectedTheme={selectedTheme}
          onAgeChange={setSelectedAge}
          onThemeChange={setSelectedTheme}
        />

        {/* Loading State */}
        {loading && (
          <div className="py-20 text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-green-500 border-t-transparent"></div>
            <p className="text-gray-600">Loading templates...</p>
          </div>
        )}

        {/* Templates Grid */}
        {!loading && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                isSubscriber={isSubscriber}
                onSelect={() => handleTemplateSelect(template)}
                onPremiumClick={handlePremiumClick}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredTemplates.length === 0 && (
          <div className="py-20 text-center">
            <Palette size={64} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600">
              No templates found. Try adjusting your filters.
            </p>
          </div>
        )}

        {/* Upsell Modal */}
        {showUpsell && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowUpsell(false)}
          >
            <div
              className="mx-4 max-w-lg rounded-2xl bg-white p-8 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="mb-4 text-2xl font-bold text-gray-800">
                Unlock Premium Features
              </h2>
              <p className="mb-6 text-gray-600">
                Get access to Montessori Mode, premium templates, and advanced
                brushes with Amaya Plus or Amaya Pro subscription.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => router.push('/pricing')}
                  className="flex-1 rounded-lg bg-gradient-to-r from-green-500 to-lime-400 px-6 py-3 font-semibold text-white transition-all hover:from-green-600 hover:to-lime-500"
                >
                  View Plans
                </button>
                <button
                  onClick={() => setShowUpsell(false)}
                  className="flex-1 rounded-lg border-2 border-gray-300 px-6 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-100"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

